import { Model } from '../types';
import './ModelList.css';

interface Props {
  models: Model[];
  onSelect: (model: Model) => void;
  favorites?: Set<string>;
  onToggleFavorite?: (canonicalId: string) => void;
}

export function ModelList({ models, onSelect, favorites, onToggleFavorite }: Props) {
  if (models.length === 0) {
    return (
      <div className="empty">
        <p>No models found matching your filters</p>
      </div>
    );
  }

  return (
    <div className="model-list">
      {models.map((model) => {
        const pricing = model.pricing?.[0];
        const score = model.quickMetrics?.overallScore;
        const platformCount = model._count?.accessChannels ?? model.accessChannels?.length ?? 0;
        const isFav = favorites?.has(model.canonicalId) ?? false;

        return (
          <div key={model.id} className="model-card" onClick={() => onSelect(model)}>
            {/* Top row: name + score + star */}
            <div className="model-top-row">
              <h3 className="model-name">{model.name}</h3>
              <div className="model-top-actions">
                {score != null && (
                  <div className="model-score">
                    <span className="score-value">{(score * 2).toFixed(1)}</span>
                    <span className="score-max">/10</span>
                  </div>
                )}
                {onToggleFavorite && (
                  <button
                    className={`fav-btn ${isFav ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(model.canonicalId); }}
                    aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {isFav ? '⭐' : '☆'}
                  </button>
                )}
              </div>
            </div>

            {/* Badges */}
            <div className="model-badges">
              {model.isNew && <span className="badge new">NEW</span>}
              {pricing?.freeTier && <span className="badge free">FREE</span>}
              {pricing?.requiresDeposit && <span className="badge deposit">$2+</span>}
              {pricing?.dataUsedForTraining && <span className="badge warning">⚠️ Trains</span>}
              {pricing?.privacyLevel === 'high' && <span className="badge privacy">🔒</span>}
              {model.isOpenSource && <span className="badge opensource">OSS</span>}
            </div>

            {/* Capability tags */}
            <div className="capability-tags">
              {model.capabilities?.some(c => (c.modality === 'text' || c.modality === 'code') && c.supported) && (
                <span className="tag text">📝 Text</span>
              )}
              {model.capabilities?.some(c => c.modality === 'image-generation' && c.supported) && (
                <span className="tag image">🖼️ Image</span>
              )}
              {model.capabilities?.some(c => c.modality === 'audio-generation' && c.supported) && (
                <span className="tag audio">🎵 Audio</span>
              )}
              {model.capabilities?.some(c => c.visionSupport) && (
                <span className="tag vision">👁️ Vision</span>
              )}
              {model.capabilities?.some(c => c.toolUseSupport) && (
                <span className="tag tools">🛠️ Tools</span>
              )}
              {model.capabilities?.some(c => c.jsonModeSupport) && (
                <span className="tag json">{ } JSON</span>
              )}
            </div>

            {/* Footer */}
            <div className="model-footer">
              <span className="model-developer">{model.developer}</span>
              <div className="model-meta">
                {model.contextWindow && (
                  <span className="meta-item">{formatContextWindow(model.contextWindow)}</span>
                )}
                {pricing?.inputPricePerMillion != null && pricing.inputPricePerMillion > 0 ? (
                  <span className="meta-item price">${pricing.inputPricePerMillion.toFixed(2)}/1M</span>
                ) : pricing?.freeTier ? (
                  <span className="meta-item free-price">Free</span>
                ) : null}
                {platformCount > 0 && (
                  <span className="meta-item platforms">📍 {platformCount}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatContextWindow(tokens: number): string {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M ctx`;
  if (tokens >= 1_000) return `${Math.round(tokens / 1_000)}K ctx`;
  return `${tokens} ctx`;
}
